import { useEffect, useRef, useState } from "react";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { customerService } from "../../services/CustomerService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FilterMatchMode } from "primereact/api";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

export default function CustomerListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const searchInputRef = useRef(null);
  const { user } = useAuth();
  const isSalesUser = user?.UserRole === "Sales";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const navigate = useNavigate();
  const toast = useRef(null);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerService.list();
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Müşteri listesi yüklenirken bir hata oluştu.",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const balanceTemplate = (rowData) => {
    const balance = rowData.balance || 0;
    const isNegative = balance < 0;
    return (
      <span className={`font-bold ${isNegative ? "text-red-500" : "text-green-600"}`}>
        {balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
      </span>
    );
  };

  const handleDeleteCustomer = (row) => {
    const hasOpeningBalance = row.openingBalance && row.openingBalance !== 0;

    confirmDialog({
      message: hasOpeningBalance
        ? `${row.name} isimli müşterinin açılış bakiyesi mevcut. Yine de silmek istiyor musunuz?`
        : "Müşteri silinecektir Emin misiniz?",
      header: "Müşteri Sil",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await customerService.delete(row.id);
          toast.current?.show({
            severity: "success",
            summary: "Başarılı",
            detail: "Müşteri başarıyla silindi.",
            life: 3000
          });
          loadCustomers();
        } catch (err) {
          console.error(err);
          toast.current?.show({
            severity: "error",
            summary: "Hata",
            detail: err.response?.data?.message || "Müşteri silinirken bir hata oluştu.",
            life: 3000
          });
        }
      }
    });
  };

  const actionsTemplate = (row) => (
    <div className="flex gap-1 justify-content-center">
      {!isSalesUser && (
        <>
        <Button
          icon="pi pi-pencil"
          rounded
          text
          severity="info"
          tooltip="Düzenle"
          className="w-8 h-8"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/customers/edit/${row.id}`);
          }}
        />
        {!row.hasTransactions && (
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            tooltip="Sil"
            className="w-8 h-8"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCustomer(row);
            }}
          />
        )}
        </>
      )}
      <Button
        icon="pi pi-eye"
        rounded
        text
        severity="help"
        tooltip="Detay"
        className="w-8 h-8"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/customers/detail/${row.id}`);
        }}
      />
    </div>
  );

  const header = (
    <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-2 py-1">
      
      <div className="flex flex-column md:flex-row gap-2 align-items-center">
        <IconField iconPosition="left" className="w-full md:w-48">
    <InputIcon className="pi pi-search text-sm"> </InputIcon>
    <InputText 
        ref={searchInputRef}
        value={globalFilterValue} 
        onChange={onGlobalFilterChange} 
        placeholder="Ara..." 
        className="p-inputtext-sm border-round-lg w-full" 
    />
</IconField>
      </div>
    </div>
  );

  return (
      <div className="bg-[#f8fafc] min-h-screen animate-fade-in">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

      <div className="bg-white shadow-lg shadow-slate-200/50 border border-slate-200 rounded-2xl overflow-hidden">
 
      <ConfirmDialog />
<div className="!bg-slate-300 border-b border-slate-200 px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="!bg-blue-600 p-4 rounded-xl shadow-md shadow-blue-500/20">
              <i className="pi pi-users text-white text-lg"></i>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-tight">Müşteri Listesi</h1>
              <p className="text-slate-400 text-xs font-bold italic">Müşteri ve Hesap Yönetimi</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              label="EXCEL'DEN IMPORT ET"
              icon="pi pi-file-excel"
              className="!bg-emerald-600 border-none hover:!bg-emerald-700 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
              onClick={() => navigate("/customers/import")}
            />
            <Button
              label="YENİ MÜŞTERİ EKLE"
              icon="pi pi-plus"
              className="!bg-blue-600 border-none hover:!bg-blue-700 px-5 py-2.5 rounded-xl font-black text-xs shadow-lg shadow-blue-500/20 transition-all active:scale-95"
              onClick={() => navigate("/customers/add")}
            />
          </div>
        </div>
 <div className="p-2 md:p-4">
        <DataTable
          value={customers}
          paginator
          rows={15}
          rowsPerPageOptions={[15, 25, 50]}
          loading={loading}
          filters={filters}
          globalFilterFields={["name", "email", "phone"]}
          header={header}
          stripedRows
          rowHover
          showGridlines
          responsiveLayout="stack"
          breakpoint="960px"
          onRowDoubleClick={(e) => navigate(`/customers/detail/${e.data.id}`)}
          className="p-datatable-sm"
          paginatorClassName="  "
        >
          <Column
            field="customerNumber"
            header="No"
            sortable
            body={(r) => <Tag value={r.customerNumber || 'N/A'} severity="info" />}
            style={{ width: '8%' }}
          />
          
          <Column
            field="name"
            header="Müşteri Adı / Ünvan"
            sortable
            className="font-bold text-900"
            style={{ width: '20%' }}
          />
          <Column
            field="note"
            header="Not"
            style={{ width: '15%' }}
            body={(row) => (
              <span className="text-xs text-slate-500 italic">
                {row.note || "-"}
              </span>
            )}
          />
          <Column
            header="Yaklaşan Ödeme"
            sortable
            field="nextDueDate"
            body={(row) => (
              <div className="flex flex-col">
                {row.nextDueDate ? (
                  <>
                    <span className="text-xs font-black text-orange-600">
                      {new Date(row.nextDueDate).toLocaleDateString("tr-TR")}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">
                      ₺{row.nextDueAmount?.toLocaleString("tr-TR")}
                    </span>
                  </>
                ) : (
                  <span className="text-slate-300">-</span>
                )}
              </div>
            )}
            style={{ width: '15%' }}
          />
          <Column field="phone" header="Telefon" style={{ width: '15%' }} />
          <Column
            field="balance"
            header="Güncel Bakiye"
            sortable
            body={balanceTemplate}
            style={{ width: '15%' }}
            align="right"
          />
          <Column
            header="Durum"
            style={{ width: '10%' }}
            align="center"
            body={(row) => (
              <Tag
                value={row.isActive ? "AKTİF" : "PASİF"}
                severity={row.isActive ? "success" : "secondary"}
                className="rounded-lg font-black text-[10px] px-3 py-1 border-none"
                style={{
                  backgroundColor: row.isActive ? '#dcfce7' : '#f1f5f9',
                  color: row.isActive ? '#166534' : '#64748b'
                }}
              />
            )}
          />
          <Column body={actionsTemplate} header="İşlemler" style={{ width: '10%' }} align="center" />
        </DataTable> 
        </div>
        </div>
    </div>
  );
}
